import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Box, DialogActions, Grid, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import EMPButton from "../../EMPButton/EMPButton";
import EMPCheckbox from "../../EMPCheckbox/EMPCheckbox";
import { ModalContext } from "../ModalContext";
import DealerActionsClient from "../../../../clients/DealerActionsClient";
import { UserContext } from "../../../context/userContext";
import { DEALER_ACTIONS } from "../../../../common/Constants";
import EMPLoader from "../../EMPLoader/EMPLoader";
import { useFieldSupportStatus } from "../../../hooks/useFieldSupportStatus";
import {
  trackAcceptPurchaseRequestPopupAction,
  trackAcceptPurchaseRequestPopupLoad,
} from "../../../services/analytics/OrderDetailsPageAnalytics";
import { usePermission } from "../../../hooks/usePermission";
import { useCanAutoAcceptDeposit } from "../../../hooks/useCanAutoAcceptDeposit";
import { useVehicleData } from "../../../hooks/useVehicleData";

const ERROR_MESSAGES = {
  DEFAULT:
    "An unexpected error occurred. Please use the back button and try again.",
  PURCHASE_REQUEST_MESSAGE:
    "Purchase Request acceptance failed. Please try again.",
  PURCHASE_DEPOSIT_MESSAGE:
    "Purchase request has been accepted. Purchase deposit was not automatically accepted. Please accept the purchase deposit manually.",
  ORDER_ACCEPTANCE: "Order acceptance failed: Unknown error",
  DEPOSIT_ACCEPTANCE: "Deposit acceptance failed: Unknown error",
};

const checkboxListItems = [
  "common.actions.acceptModal.checkboxList.0",
  "common.actions.acceptModal.checkboxList.1",
  "common.actions.acceptModal.checkboxList.2",
  "common.actions.acceptModal.checkboxList.3",
  "common.actions.acceptModal.checkboxList.4",
  "common.actions.acceptModal.checkboxList.5",
];

const parseErrorData = (errorData) => {
  if (!errorData) return null;

  try {
    const parsed =
      typeof errorData === "string" ? JSON.parse(errorData) : errorData;

    return parsed.errors?.[0]?.message;
  } catch (e) {
    console.error("Failed to parse error message:", e);
    return null;
  }
};

const handleOtherErrorData = (otherErrorData) => {
  if (!otherErrorData) return null;

  if (
    typeof otherErrorData === "string" &&
    otherErrorData === ERROR_MESSAGES.ORDER_ACCEPTANCE
  ) {
    return ERROR_MESSAGES.PURCHASE_REQUEST_MESSAGE;
  }

  if (
    typeof otherErrorData === "string" &&
    otherErrorData === ERROR_MESSAGES.DEPOSIT_ACCEPTANCE
  ) {
    return ERROR_MESSAGES.PURCHASE_DEPOSIT_MESSAGE;
  }

  return otherErrorData;
};

const AcceptPurchaseRequest = ({ orderId, backButton, handleBack }) => {
  const { handleModal } = useContext(ModalContext);
  const { user } = useContext(UserContext);
  const { market, dealerId, salesCode, brand, userId } = user;
  const userCommonId = `${market}|${dealerId}|${salesCode}|${brand}`;
  const [checked, setChecked] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);
  const isFieldSupport = useFieldSupportStatus();
  const canAutoAcceptDeposit = useCanAutoAcceptDeposit(orderId);
  const canAcceptPurchaseRequest = usePermission(
    "acceptPurchaseRequestNA:write"
  );
  const { nameplate, modelYear } = useVehicleData(orderId);

  const { customerReservation } = queryClient.getQueryData([
    "order",
    { orderId },
  ]);

  const { paymentType, orderConfirmedDate, todayDate } = customerReservation;
  const orderCreatedDate = new Date(orderConfirmedDate).toISOString();

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    trackAcceptPurchaseRequestPopupLoad(orderId, user, modelYear, nameplate);
  }, [orderId, user, modelYear, nameplate]);

  const submitStatus = useMutation({
    mutationFn: (actionData) => {
      if (canAutoAcceptDeposit) {
        return DealerActionsClient.acceptRequestAndDeposit(
          userCommonId,
          actionData,
          userId
        );
      }

      return DealerActionsClient.actionOnOrder(
        userCommonId,
        actionData,
        userId
      );
    },
    onSuccess: () => {
      trackAcceptPurchaseRequestPopupAction(
        paymentType,
        orderId,
        orderCreatedDate,
        user,
        modelYear,
        nameplate
      );

      handleModal(false, false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["order", { orderId }] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", { orderId }],
      });
    },
    onError: (error) => {
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message;

        console.log(`400 Error: ${errorMessage}`);
        return;
      }

      console.log(error);
    },
  });

  const isSubmitPending = submitStatus.status === "pending";
  const isSubmitError = submitStatus.status === "error";

  const handleChange = (checkedBool) => {
    setChecked(checkedBool);

    if (isFieldSupport) {
      return;
    }

    setDisabled(!checkedBool);
  };

  const handleErrorMessage = () => {
    if (!submitStatus.error?.response) {
      return ERROR_MESSAGES.DEFAULT;
    }

    const { error, data } = submitStatus.error.response;

    if (error) return error;

    const parsedMessage = parseErrorData(data?.message);
    if (parsedMessage) return parsedMessage;

    const otherError = handleOtherErrorData(data?.error);
    if (otherError) return otherError;

    return ERROR_MESSAGES.DEFAULT;
  };

  const handleCancelOrBack = () => {
    if (backButton) {
      handleBack();
      return;
    }

    handleModal(false, false);
  };

  const handleSubmit = () => {
    submitStatus.mutate({
      reservationId: orderId,
      acceptedDate: todayDate,
      orderCreatedDate,
      dealerAction: DEALER_ACTIONS.PURCHASE_REQUEST_ACCEPTED,
    });
  };

  const checkboxList = checkboxListItems.map((item) => (
    <li key={item}>
      <Typography variant="body2">{t(item)}</Typography>
    </li>
  ));

  if (isSubmitPending) {
    return (
      <Box sx={{ minHeight: contentHeight, py: 10 }}>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ height: "100%" }}
        >
          <Grid item>
            <EMPLoader loadingMessage="Submitting..." />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (isSubmitError) {
    return (
      <Box>
        <Box sx={{ minHeight: contentHeight, pb: 4 }}>
          <Typography variant="body1">{handleErrorMessage()}</Typography>
        </Box>

        <DialogActions sx={{ padding: "16px 0" }}>
          <Grid container justifyContent="space-between">
            <Grid item>
              {!canAutoAcceptDeposit && (
                <EMPButton
                  variant="text"
                  onClick={handleBack}
                  testId="modal-cancel-back-button"
                >
                  Back
                </EMPButton>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Box>
    );
  }

  return (
    <Box ref={contentRef}>
      <Typography variant="body1" sx={{ mb: 6 }}>
        {t("common.actions.acceptModal.message")}
      </Typography>

      <Box sx={{ mb: 10 }}>
        <ul style={{ paddingLeft: "12px" }}>{checkboxList}</ul>
      </Box>

      <Box sx={{ mb: 4 }}>
        <EMPCheckbox
          label={t("common.actions.acceptModal.checkboxConfirm")}
          checked={checked}
          handleChange={handleChange}
        />
      </Box>

      <DialogActions sx={{ padding: "16px 0" }}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <EMPButton
              variant="text"
              onClick={handleCancelOrBack}
              disabled={isSubmitPending}
              testId="modal-cancel-back-button"
            >
              {backButton ? "Back" : t("common.cancel")}
            </EMPButton>
          </Grid>

          <Grid item>
            <EMPButton
              variant="contained"
              onClick={handleSubmit}
              disabled={
                disabled ||
                isSubmitPending ||
                isSubmitError ||
                !canAcceptPurchaseRequest
              }
            >
              {t("common.buttonCtas.acceptPurchaseRequest")}
            </EMPButton>
          </Grid>
        </Grid>
      </DialogActions>
    </Box>
  );
};

AcceptPurchaseRequest.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  backButton: PropTypes.bool,
  handleBack: PropTypes.func,
};

export default AcceptPurchaseRequest;
